<script setup lang="ts">
import type { DropdownMenuItem } from '@nuxt/ui'
import { brandConfig } from '~/config/brand'
import type { ThemePresetId, ThemePresetMeta } from '~/theme/presets'

defineProps<{
  collapsed?: boolean
}>()

const colorMode = useColorMode()
const route = useRoute()
const auth = useAuth()
const { currentPresetId, presets, setPreset } = useTheme()

const user = ref({
  name: brandConfig.user.name,
  avatar: {
    src: brandConfig.user.avatar.src,
    alt: brandConfig.user.avatar.alt,
  },
})

const handleLogout = () => {
  auth.logout()
}

const themeMenuItems = computed(() =>
  presets.map((preset: ThemePresetMeta) => ({
    label: preset.name,
    type: 'checkbox' as const,
    checked: currentPresetId.value === preset.id,
    onSelect(e: Event) {
      e.preventDefault()
      setPreset(preset.id as ThemePresetId)
    },
  }))
)

const items = computed<DropdownMenuItem[][]>(() => [
  [
    {
      type: 'label',
      label: user.value.name,
      avatar: user.value.avatar,
    },
  ],
  [
    {
      label: 'Settings',
      icon: 'i-lucide-settings',
      to: '/settings',
      // Nuxt UI marks the current route with colour-mode colours, unreadable on a dropdown
      // painted by the preset. `activeClass` cannot replace them: the link renders in ULink's
      // `custom` mode, where only the per item `class` arrives.
      class: route.path.startsWith('/settings') ? 'sidebar-dropdown-item-active' : undefined,
    },
    {
      label: 'Appearance',
      icon: 'i-lucide-sun-moon',
      children: [
        {
          label: 'Light',
          icon: 'i-lucide-sun',
          type: 'checkbox',
          checked: colorMode.value === 'light',
          onSelect(e: Event) {
            e.preventDefault()

            colorMode.preference = 'light'
          },
        },
        {
          label: 'Dark',
          icon: 'i-lucide-moon',
          type: 'checkbox',
          checked: colorMode.value === 'dark',
          onUpdateChecked(checked: boolean) {
            if (checked) {
              colorMode.preference = 'dark'
            }
          },
          onSelect(e: Event) {
            e.preventDefault()
          },
        },
      ],
    },
    {
      label: 'Color Theme',
      icon: 'i-lucide-palette',
      children: themeMenuItems.value,
    },
  ],
  [
    {
      label: 'Log out',
      icon: 'i-lucide-log-out',
      onSelect: handleLogout,
    },
  ],
])
</script>

<template>
  <UDropdownMenu
    :items="items"
    :content="{ align: 'center', collisionPadding: 12 }"
    :ui="{
      content: [
        'sidebar-dropdown-bg',
        'ring-[var(--preset-sidebar-dropdown-border)]',
        collapsed ? 'w-48' : 'w-(--reka-dropdown-menu-trigger-width)',
      ],
      group: 'sidebar-dropdown-group',
      item: 'sidebar-dropdown-item',
      itemLeadingIcon: 'sidebar-dropdown-icon',
      label: 'sidebar-dropdown-label',
      separator: 'sidebar-dropdown-separator',
    }"
  >
    <UButton
      v-bind="{
        ...user,
        label: collapsed ? undefined : user?.name,
        trailingIcon: collapsed ? undefined : 'i-lucide-chevrons-up-down',
      }"
      color="neutral"
      variant="ghost"
      block
      :square="collapsed"
      class="sidebar-user-btn"
      data-testid="user-menu"
      :ui="{
        trailingIcon: 'text-dimmed',
      }"
    />
  </UDropdownMenu>
</template>
